import { Component, Input, OnInit, signal } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { SelectItem } from 'primeng/api';
import { BaseOperation } from 'src/app/perical/models/thermal-operation.model';
import { FluidsService } from 'src/app/perical/services/fluids.service';
import { PoreFrac } from 'src/app/perivis/models/poreFrac.model';
import { WellType } from 'src/app/shared/models/wellType.model';
import { WellComponent } from 'src/app/wellbore-inputs/components/well-configuration/models';
import { CementingLanding } from 'src/app/wellbore-inputs/models/cementing-landing.model';
import { CasingFullOfGasIPP, CirculatedKickGasIPP, CirculatedKickLiquidIPP, CustomIPP, DisplacementFluidIPP,
   FullEvacIPP, GasOverMudIPP, GeneralFluidIPP, InternalPressureProfile, InternalPressureProfileType, LostReturnsFluidDropIPP,
   LostReturnsWaterIPP, MudOverGasIPP, PartialEvacByMdIPP, PericalLinkIPP, PressurePoint, PressureTestIPP, SssvIPP, WCDIPP } from '../models/internal-pressure-profile.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { LoadCaseService } from 'src/app/perivis/services/load-case.service';

export interface InternalPressureProfileForm {
  type: InternalPressureProfileType;
  customIpp: CustomIPP;
  pericalLinkIpp: PericalLinkIPP;
  displacementFluidIpp: DisplacementFluidIPP;
  pressureTestIpp: PressureTestIPP;
  sssvIpp: SssvIPP;
  generalFluidIpp: GeneralFluidIPP;
  fullEvacIpp: FullEvacIPP;
  partialEvacByMdIpp: PartialEvacByMdIPP;
  wcdIpp: WCDIPP;
  lostReturnsFluidDropIpp: LostReturnsFluidDropIPP;
  lostReturnsWaterIpp: LostReturnsWaterIPP;
  circulatedKickGasIpp: CirculatedKickGasIPP;
  circulatedKickLiquidIpp: CirculatedKickLiquidIPP;
  cfogIpp: CasingFullOfGasIPP;
  gasOverMudIpp: GasOverMudIPP;
  mudOverGasIpp: MudOverGasIPP;
}

@Component({
    selector: 'app-internal-pressure-profiles',
    templateUrl: './internal-pressure-profiles.component.html',
    styles: [`
      ::ng-deep .p-panel-content-wrapper {
        width: 100%;
        max-width: 100%;
        min-width: 0;
      }
    `],
    providers: subformComponentProviders(InternalPressureProfilesComponent),
    standalone: false
})
export class InternalPressureProfilesComponent implements OnInit {

  @Input() thermalOperations: Partial<BaseOperation>[];
  @Input() cement: CementingLanding;
  @Input() currentString: any;
  @Input() wellType: WellType;
  @Input() userUnits: UserUnitsModel;
  @Input() wellTd: number;
  @Input() isReadOnly: boolean;
  @Input() porePressures: PoreFrac[];
  @Input() fracPressures: PoreFrac[];
  @Input() tubulars: WellComponent[];
  @Input() loadCaseIndex: number;

  public defaultPressures: PressurePoint[] = [];
  public influxFracDepthRange: { min: number, max: number };
  public fractureDepthDefault: number;

  public form = createForm<InternalPressureProfile, InternalPressureProfileForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(null, [Validators.required]),
      customIpp: new UntypedFormControl(null),
      pericalLinkIpp: new UntypedFormControl(null),
      displacementFluidIpp: new UntypedFormControl(null),
      pressureTestIpp: new UntypedFormControl(null),
      sssvIpp: new UntypedFormControl(null),
      generalFluidIpp: new UntypedFormControl(null),
      fullEvacIpp: new UntypedFormControl(null),
      partialEvacByMdIpp: new UntypedFormControl(null),
      wcdIpp: new UntypedFormControl(null),
      lostReturnsFluidDropIpp: new UntypedFormControl(null),
      lostReturnsWaterIpp: new UntypedFormControl(null),
      circulatedKickGasIpp: new UntypedFormControl(null),
      circulatedKickLiquidIpp: new UntypedFormControl(null),
      cfogIpp: new UntypedFormControl(null),
      gasOverMudIpp: new UntypedFormControl(null),
      mudOverGasIpp: new UntypedFormControl(null)
    },
    toFormGroup: (obj: InternalPressureProfile | null): InternalPressureProfileForm | null => {
      if (!obj) {
        return null;
      }

      return {
        type: obj.type,
        customIpp: obj.type === InternalPressureProfileType.CUSTOMIPP ? obj : null,
        pericalLinkIpp: obj.type === InternalPressureProfileType.PERICALLINK ? obj : null,
        displacementFluidIpp: obj.type === InternalPressureProfileType.DISPLACEMENTFLUID ? obj : null,
        pressureTestIpp: obj.type === InternalPressureProfileType.PRESSURETEST ? obj : null,
        sssvIpp: obj.type === InternalPressureProfileType.SSSV ? obj : null,
        generalFluidIpp: obj.type === InternalPressureProfileType.GENERALFLUID ? obj : null,
        fullEvacIpp: obj.type === InternalPressureProfileType.FULLEVAC ? obj : null,
        partialEvacByMdIpp: obj.type === InternalPressureProfileType.PARTIALEVAC ? obj : null,
        wcdIpp: obj.type === InternalPressureProfileType.WCDIPP ? obj : null,
        lostReturnsFluidDropIpp: obj.type === InternalPressureProfileType.FLUIDDROP ? obj : null,
        lostReturnsWaterIpp: obj.type === InternalPressureProfileType.LOSTRETURNSWATER ? obj : null,
        circulatedKickGasIpp: obj.type === InternalPressureProfileType.GASKICK ? obj : null,
        circulatedKickLiquidIpp: obj.type === InternalPressureProfileType.LIQUIDKICK ? obj : null,
        cfogIpp: obj.type === InternalPressureProfileType.CFOG ? obj : null,
        gasOverMudIpp: obj.type === InternalPressureProfileType.GASOVERMUD ? obj : null,
        mudOverGasIpp: obj.type === InternalPressureProfileType.MUDOVERGAS ? obj : null
      };
    },
    fromFormGroup: (formValue: InternalPressureProfileForm): InternalPressureProfile | null => {
      const { type, customIpp, pericalLinkIpp, displacementFluidIpp, pressureTestIpp, sssvIpp, generalFluidIpp,
         fullEvacIpp, partialEvacByMdIpp, wcdIpp, lostReturnsFluidDropIpp, lostReturnsWaterIpp, circulatedKickGasIpp,
         circulatedKickLiquidIpp, cfogIpp, gasOverMudIpp, mudOverGasIpp, ...commonValues } = formValue;

      switch (type) {
        case InternalPressureProfileType.CUSTOMIPP:
          return customIpp ? { type, ...customIpp, ...commonValues } : null;
        case InternalPressureProfileType.PERICALLINK:
          return pericalLinkIpp ? { type, ...pericalLinkIpp, ...commonValues } : null;
        case InternalPressureProfileType.DISPLACEMENTFLUID:
          return displacementFluidIpp ? { type, ...displacementFluidIpp, ...commonValues } : null;
        case InternalPressureProfileType.PRESSURETEST:
          return pressureTestIpp ? { type, ...pressureTestIpp, ...commonValues } : null;
        case InternalPressureProfileType.SSSV:
          return sssvIpp ? { type, ...sssvIpp, ...commonValues } : null;
        case InternalPressureProfileType.GENERALFLUID:
          return generalFluidIpp ? { type, ...generalFluidIpp, ...commonValues } : null;
        case InternalPressureProfileType.FULLEVAC:
          return fullEvacIpp ? { type, ...fullEvacIpp, ...commonValues } : null;
        case InternalPressureProfileType.PARTIALEVAC:
          return partialEvacByMdIpp ? { type, ...partialEvacByMdIpp, ...commonValues } : null;
        case InternalPressureProfileType.WCDIPP:
          return wcdIpp ? { type, ...wcdIpp, ...commonValues } : null;
        case InternalPressureProfileType.FLUIDDROP:
          return lostReturnsFluidDropIpp ? { type, ...lostReturnsFluidDropIpp, ...commonValues } : null;
        case InternalPressureProfileType.LOSTRETURNSWATER:
          return lostReturnsWaterIpp ? { type, ...lostReturnsWaterIpp, ...commonValues } : null;
        case InternalPressureProfileType.GASKICK:
          return circulatedKickGasIpp ? { type, ...circulatedKickGasIpp, ...commonValues } : null;
        case InternalPressureProfileType.LIQUIDKICK:
          return circulatedKickLiquidIpp ? { type, ...circulatedKickLiquidIpp, ...commonValues } : null;
        case InternalPressureProfileType.CFOG:
          return cfogIpp ? { type, ...cfogIpp, ...commonValues } : null;
        case InternalPressureProfileType.GASOVERMUD:
          return gasOverMudIpp ? { type, ...gasOverMudIpp, ...commonValues } : null;
        case InternalPressureProfileType.MUDOVERGAS:
          return mudOverGasIpp ? { type, ...mudOverGasIpp, ...commonValues } : null;
      }
    }
  });

  public ipps: SelectItem[] = [
    { label: 'Custom', value: 'CustomInternalPressures' },
    { label: 'PERICAL Link', value: 'ThermalLink' },
    // { label: 'Displacement Fluid', value: 'DisplacementFluid' },
    { label: 'Pressure Test', value: 'PressureTest' },
    { label: 'SSSV/DHCV Test', value: 'SubSurfaceSafetyValveTest' },
    { label: 'General Fluid Profile', value: 'GeneralFluidProfile' },
    { label: 'Full Evacuation', value: 'FullEvacuation' },
    { label: 'Partial Evacuation By MD', value: 'PartialEvacuationByMd' },
    // { label: 'Partial Evacuation By TVD', value: { id: 'PartialEvacuationByTvd' } },
    { label: 'Lost Returns (Fluid Drop)', value: 'FluidDrop' },
    { label: 'Lost Returns With Water', value: 'LostReturnsWithWater' },
    { label: 'Circulated Kick: Gas', value: 'GasKick' },
    { label: 'Circulated Kick: Liquid', value: 'LiquidKick' },
    { label: 'Gas Influx: Casing Full Of Gas', value: 'CasingFullOfGas' },
    { label: 'Gas Influx: Gas Over Mud', value: 'GasOverMud' },
    { label: 'Gas Influx: Mud Over Gas', value: 'MudOverGas' },
    { label: 'Worst Case Discharge', value: 'WorstCaseDischarge' }
  ];

  public InternalPressureProfileType: typeof InternalPressureProfileType = InternalPressureProfileType;
  public nextStringMudWeight: number;
  public currentStringMudWeight: number;
  public smallestPipeId: number;
  public isLoading = signal<boolean>(false);

  constructor(
    private _fluidsService: FluidsService,
    private _loadCaseService: LoadCaseService) {}

  ngOnInit(): void {
    if (this.currentString.type == 'Tubing' && !this.currentString?.hasCement) {
      this.ipps.splice(2, 0, { label: 'Completion Fluid', value: 'DisplacementFluid' });
    } else {
      this.ipps.splice(2, 0, { label: 'Displacement Fluid', value: 'DisplacementFluid' });
    }

    this.setDefaults();
  }

  private setDefaults() {
    const selectedStringIndex = this.tubulars.findIndex(x => x.id === this.currentString.id);
    const nextString = this.tubulars[selectedStringIndex + 1];
    const lastStringIndex = this.tubulars.length - 1;
    const lastString = this.tubulars[lastStringIndex];

    if (selectedStringIndex === lastStringIndex && lastString.type === 'Tubing') {
      const excludedValues = ["GasKick", "LiquidKick", "FluidDrop", "LostReturnsWithWater", "CasingFullOfGas", "GasOverMud", "MudOverGas"];
      this.ipps = this.ipps.filter(x => !excludedValues.includes(x.value));
    }

    this.smallestPipeId = this.currentString.stringSections[this.currentString.stringSections.length - 1].pipe.insideDiameter;
    this.currentStringMudWeight = this.currentString.annularFluid?.state['nominalDensity'] ?? 0;
    const nextShoeDepth = Math.max(nextString?.shoeMd ?? 0, this.currentString.shoeMd);
    switch (this.currentString.type) {
      case 'Casing': {
        this.checkNextLinerShoeDepth(selectedStringIndex, nextShoeDepth);
        break;
      }
      case 'Liner': {
        this.checkNextLinerShoeDepth(selectedStringIndex, nextShoeDepth);
        break;
      }
      case 'Tieback': {
        const previousString = this.tubulars[selectedStringIndex - 1];
        const previousStringIndex = this.tubulars.findIndex(t => t.shoeMd === previousString.shoeMd) - 1;
        this.checkNextLinerShoeDepth(previousStringIndex, previousString.shoeMd);
        break;
      }
      case 'Tubing': {
        this.nextStringMudWeight = this.currentString.annularFluid?.state['nominalDensity'] ?? 0;
        break;
      }
    }
  }

  private checkNextLinerShoeDepth(selectedStringIndex: number, nextOrLinerShoeDepth: number): void {
    let deepestNextLiner = null;
    let tubular = null;
    for (let i = selectedStringIndex + 1; i < this.tubulars.length; i++) {
      tubular = this.tubulars[i];
      if (tubular.type === 'Liner') {
          deepestNextLiner = tubular.shoeMd;
          break;
      } else if (tubular.type === 'Tieback') {
        const linerItsTiedBackTo = this.tubulars.find(t =>
          t.type === 'Liner' &&
          t.hangerMd === tubular.shoeMd
        ).shoeMd;
        deepestNextLiner = linerItsTiedBackTo;
        break;
      } else {
        deepestNextLiner = tubular.shoeMd;
        break;
      }
    }

    this.influxFracDepthRange = { 
      min: this.currentString.type == 'Tieback' ? nextOrLinerShoeDepth : this.currentString.shoeMd, 
      max: deepestNextLiner ?? this.currentString.shoeMd
    };

    this.updateRangeAndFluidDensity();
  }

  private updateRangeAndFluidDensity(): void {
    const currentStringIndex = this.tubulars.findIndex(t => t.id === this.currentString.id);
    
    this.fractureDepthDefault = this.influxFracDepthRange.min;
    let maxValidation = this.fractureDepthDefault;
    let annularFluidId = null;
    for (let i = currentStringIndex; i < this.tubulars.length; i++) {
      const tubular = this.tubulars[i];
      if ((tubular.shoeMd > this.influxFracDepthRange.max && i == tubular.sequenceNumber) ||
       (tubular.shoeMd >= this.influxFracDepthRange.max && i < tubular.sequenceNumber) ||
       (tubular.shoeMd >= this.influxFracDepthRange.max && i == tubular.sequenceNumber && tubular.type != 'Tubing')
      ) { 
        annularFluidId = tubular.annularFluidId;

        if (tubular.type == 'Tieback') {
          maxValidation = this.tubulars[i - 1].shoeMd;
          break;
        } else if (tubular.type == 'Liner') {
          maxValidation = tubular.shoeMd;

        for (let j = i - 1; j >= 0; j--) {
          if (this.tubulars[j].type == 'Tieback') {
            continue;
          } else {
            this.fractureDepthDefault = this.tubulars[j].shoeMd;
            break;
          }
        }
          continue;
        } else if (tubular.type == 'Casing') {
          maxValidation = tubular.shoeMd;
        } else if (tubular.type == 'Tubing') {
          this.influxFracDepthRange.max = maxValidation;
          break;
        }

        if (this.tubulars[i].type == 'Tieback') {
          const linerItsTiedBackTo = this.tubulars.find(t =>
            t.type === 'Liner' &&
            t.hangerMd === tubular.shoeMd
          ).shoeMd;
          this.fractureDepthDefault = linerItsTiedBackTo;
        } else if (this.tubulars[i - 1] != null) {
          this.fractureDepthDefault = this.tubulars[i - 1].shoeMd;
        }
        break;
      } else if (tubular.type == 'Tieback') {
        const selectedStringIndex = this.tubulars.findIndex(t => t.id === this.currentString.id);
        const tiedBackLinerIndex = this.tubulars.findIndex(t =>
          t.type === 'Liner' &&
          t.hangerMd === tubular.shoeMd
        );

        if (selectedStringIndex < tiedBackLinerIndex) {
          maxValidation = this.tubulars[i - 1].shoeMd;
          break;
        }

        maxValidation = this.tubulars[i - 1].shoeMd;
        continue;
      }
    }

    this.influxFracDepthRange.max = maxValidation;

    if (annularFluidId && annularFluidId !== this.currentString.annularFluidId) {
      this._fluidsService.getFluidById(annularFluidId).subscribe(fluid => {
        this.nextStringMudWeight = fluid.state['nominalDensity'];
      });
    } else {
      this.nextStringMudWeight = this.currentString.annularFluid?.state['nominalDensity'] ?? 0;
    }

    this.influxFracDepthRange = { 
      min: +this.influxFracDepthRange.min,
      max: +this.influxFracDepthRange.max
    };

    this.fractureDepthDefault = Math.max(this.influxFracDepthRange.min, this.fractureDepthDefault);
  }

  public onIppTypeChange(event: any) {
    if (event.value === InternalPressureProfileType.CUSTOMIPP) {
      this.isLoading.set(true);
      this._loadCaseService.getPressuresAndTemperatures(this.loadCaseIndex).subscribe({
        next: (data) => {
          this.defaultPressures = data.internalPressurePoints
            .filter((point, index, self) => 
              index === self.findIndex((p) => p.measuredDepth === point.measuredDepth)
            );
          this.isLoading.set(false);
        },
        error: () => {
          this.defaultPressures = [{ measuredDepth: 0, pressure: 0 }];
          this.isLoading.set(false);
        }
      });
    } else {
      this.setDefaults();
    }
  }
}